import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  BarChart3,
  Building2,
  CheckCircle,
  Users,
  Hourglass,
  Mail,
  XCircle,
  Copy,
  ExternalLink,
  Pencil,
  Search,
  X,
  CalendarClock,
} from "lucide-react";
import type {
  AdminWorkspaceCampaignDetailResponse,
  AdminWorkspaceCampaignListResponse,
  AdminWorkspaceDetailResponse,
  AdminWorkspaceListItemResponse,
  AdminWorkspaceListResponse,
  AdminWorkspaceEmailItemResponse,
} from "@/lib/api/generated";
import {
  getAdminWorkspaceCampaignDetailServer,
  getAdminWorkspaceCampaignsServer,
  getAdminWorkspaceDetailServer,
  getAdminWorkspacesServer,
  getAdminWorkspaceEmailsServer,
  updateAdminWorkspaceCampaignBusinessServer,
  updateAdminWorkspaceCampaignIcpServer,
  getWorkspaceTrialStatusServer,
  setWorkspaceTrialServer,
  type AdminWorkspaceEmailListResponse,
  type TrialStatusResponse,
  type TrialSetResponse,
} from "@/server/admin";
import { logError } from "@/lib/structured-log";
import { extractApiErrorMessage } from "@/lib/api/error";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmailDetailDialog } from "@/components/admin/email-detail-dialog";

interface SearchParams {
  page?: number;
  query?: string;
  sortBy?: "created_at" | "campaign_count";
  workspaceId?: string;
  campaignPage?: number;
  campaignId?: string;
  emailPage?: number;
  emailDirection?: "outbound" | "inbound";
}

const PAGE_SIZE = 20;
const CAMPAIGN_PAGE_SIZE = 10;
const EMAIL_PAGE_SIZE = 20;

export const Route = createFileRoute("/admin/workspaces")({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    query: typeof search.query === "string" ? search.query : undefined,
    sortBy:
      search.sortBy === "campaign_count" || search.sortBy === "created_at"
        ? search.sortBy
        : "campaign_count",
    workspaceId: typeof search.workspaceId === "string" ? search.workspaceId : undefined,
    campaignPage: Number(search.campaignPage) || 1,
    campaignId: typeof search.campaignId === "string" ? search.campaignId : undefined,
    emailPage: Number(search.emailPage) || 1,
    emailDirection:
      search.emailDirection === "outbound" || search.emailDirection === "inbound"
        ? search.emailDirection
        : undefined,
  }),
  loaderDeps: ({ search }) => ({
    page: search.page || 1,
    query: search.query,
    sortBy: search.sortBy || "campaign_count",
  }),
  loader: async ({ deps }) => {
    const skip = ((deps.page || 1) - 1) * PAGE_SIZE;

    const list = await getAdminWorkspacesServer({
      data: {
        skip,
        limit: PAGE_SIZE,
        query: deps.query,
        sort_by: deps.sortBy || "campaign_count",
      },
    });

    return {
      list: list as AdminWorkspaceListResponse,
    };
  },
  component: AdminWorkspacesPage,
});

function AdminWorkspacesPage() {
  const { t } = useTranslation("admin");
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { list } = Route.useLoaderData();

  const currentPage = search.page || 1;
  const currentQuery = search.query || "";
  const currentSortBy = search.sortBy || "campaign_count";
  const currentCampaignPage = search.campaignPage || 1;
  const currentCampaignId = search.campaignId;
  const currentEmailPage = search.emailPage || 1;
  const currentEmailDirection = search.emailDirection;
  const [queryInput, setQueryInput] = useState<string>(currentQuery);
  const [detail, setDetail] = useState<AdminWorkspaceDetailResponse | null>(null);
  const [campaigns, setCampaigns] = useState<AdminWorkspaceCampaignListResponse | null>(null);
  const [campaignDetail, setCampaignDetail] = useState<AdminWorkspaceCampaignDetailResponse | null>(
    null,
  );
  const [campaignDetailError, setCampaignDetailError] = useState<string | null>(null);
  const [emails, setEmails] = useState<AdminWorkspaceEmailListResponse | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<AdminWorkspaceEmailItemResponse | null>(null);
  const [isEmailDetailOpen, setIsEmailDetailOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);
  const [isCampaignsLoading, setIsCampaignsLoading] = useState<boolean>(false);
  const [isCampaignDetailLoading, setIsCampaignDetailLoading] = useState<boolean>(false);
  const [isEmailsLoading, setIsEmailsLoading] = useState<boolean>(false);
  const lastWorkspaceIdRef = useRef<string | undefined>(undefined);

  const items = list.items;
  const totalPages = useMemo(() => {
    if (!list.total) return 1;
    return Math.max(1, Math.ceil(list.total / PAGE_SIZE));
  }, [list.total]);

  const campaignTotalPages = useMemo(() => {
    if (!campaigns?.total) return 1;
    return Math.max(1, Math.ceil(campaigns.total / CAMPAIGN_PAGE_SIZE));
  }, [campaigns?.total]);

  const emailTotalPages = useMemo(() => {
    if (!emails?.total) return 1;
    return Math.max(1, Math.ceil(emails.total / EMAIL_PAGE_SIZE));
  }, [emails?.total]);

  useEffect(() => {
    if (search.workspaceId || items.length === 0) return;

    navigate({
      search: {
        page: currentPage,
        query: currentQuery || undefined,
        sortBy: currentSortBy,
        workspaceId: items[0]?.id,
        campaignPage: 1,
        campaignId: undefined,
      },
      replace: true,
    });
  }, [currentPage, currentQuery, currentSortBy, items, navigate, search.workspaceId]);

  useEffect(() => {
    const workspaceId = search.workspaceId;
    if (!workspaceId) {
      lastWorkspaceIdRef.current = undefined;
      setDetail(null);
      setCampaigns(null);
      setCampaignDetail(null);
      setCampaignDetailError(null);
      setIsDetailLoading(false);
      setIsCampaignsLoading(false);
      return;
    }

    let cancelled = false;
    const campaignSkip = (currentCampaignPage - 1) * CAMPAIGN_PAGE_SIZE;
    const workspaceChanged = lastWorkspaceIdRef.current !== workspaceId;
    lastWorkspaceIdRef.current = workspaceId;

    setIsDetailLoading(true);
    setIsCampaignsLoading(true);
    if (workspaceChanged) {
      setCampaignDetail(null);
      setCampaignDetailError(null);
    }

    const detailPromise = getAdminWorkspaceDetailServer({
      data: { workspaceId },
    });
    const campaignsPromise = getAdminWorkspaceCampaignsServer({
      data: {
        workspaceId,
        skip: campaignSkip,
        limit: CAMPAIGN_PAGE_SIZE,
      },
    });

    Promise.allSettled([detailPromise, campaignsPromise])
      .then(([detailResult, campaignsResult]) => {
        if (cancelled) return;

        if (detailResult.status === "fulfilled") {
          setDetail(detailResult.value as AdminWorkspaceDetailResponse);
        } else {
          setDetail(null);
          logError("admin.workspaces.detail_load_failed", {
            workspace_id: workspaceId,
            error:
              detailResult.reason instanceof Error ? detailResult.reason.message : "unknown-error",
          });
        }

        if (campaignsResult.status === "fulfilled") {
          setCampaigns(campaignsResult.value as AdminWorkspaceCampaignListResponse);
        } else {
          setCampaigns(null);
          logError("admin.workspaces.campaigns_load_failed", {
            workspace_id: workspaceId,
            error:
              campaignsResult.reason instanceof Error
                ? campaignsResult.reason.message
                : "unknown-error",
            skip: campaignSkip,
            limit: CAMPAIGN_PAGE_SIZE,
          });
        }
      })
      .finally(() => {
        if (cancelled) return;
        setIsDetailLoading(false);
        setIsCampaignsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentCampaignPage, search.workspaceId]);

  useEffect(() => {
    const workspaceId = search.workspaceId;
    const campaignId = currentCampaignId;
    if (!workspaceId || !campaignId) {
      setCampaignDetail(null);
      setCampaignDetailError(null);
      setIsCampaignDetailLoading(false);
      return;
    }

    let cancelled = false;
    setIsCampaignDetailLoading(true);
    setCampaignDetail(null);
    setCampaignDetailError(null);

    getAdminWorkspaceCampaignDetailServer({
      data: {
        workspaceId,
        campaignId,
      },
    })
      .then((result) => {
        if (cancelled) return;
        setCampaignDetail(result as AdminWorkspaceCampaignDetailResponse);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const errorMessage = parseErrorMessage(error);
        setCampaignDetailError(errorMessage);
        logError("admin.workspaces.campaign_detail_load_failed", {
          workspace_id: workspaceId,
          campaign_id: campaignId,
          error: errorMessage,
        });
      })
      .finally(() => {
        if (cancelled) return;
        setIsCampaignDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentCampaignId, search.workspaceId]);

  // Load emails when workspace changes or email pagination/filter changes
  useEffect(() => {
    const workspaceId = search.workspaceId;
    if (!workspaceId) {
      setEmails(null);
      setIsEmailsLoading(false);
      return;
    }

    let cancelled = false;
    const emailSkip = (currentEmailPage - 1) * EMAIL_PAGE_SIZE;

    setIsEmailsLoading(true);

    getAdminWorkspaceEmailsServer({
      data: {
        workspaceId,
        skip: emailSkip,
        limit: EMAIL_PAGE_SIZE,
        direction: currentEmailDirection,
      },
    })
      .then((result) => {
        if (cancelled) return;
        setEmails(result as AdminWorkspaceEmailListResponse);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const errorMessage = parseErrorMessage(error);
        logError("admin.workspaces.emails_load_failed", {
          workspace_id: workspaceId,
          error: errorMessage,
          skip: emailSkip,
          limit: EMAIL_PAGE_SIZE,
        });
        setEmails(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsEmailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentEmailPage, currentEmailDirection, search.workspaceId]);

  const handlePageChange = (nextPage: number) => {
    const safePage = Math.min(Math.max(1, nextPage), totalPages);
    navigate({
      search: {
        page: safePage,
        query: currentQuery || undefined,
        sortBy: currentSortBy,
        workspaceId: undefined,
        campaignPage: undefined,
        campaignId: undefined,
        emailPage: undefined,
        emailDirection: undefined,
      },
    });
  };

  const handleSortByChange = (sortBy: "created_at" | "campaign_count") => {
    navigate({
      search: {
        page: 1,
        query: currentQuery || undefined,
        sortBy,
        workspaceId: undefined,
        campaignPage: undefined,
        campaignId: undefined,
        emailPage: undefined,
        emailDirection: undefined,
      },
    });
  };

  const handleSelectWorkspace = (workspaceId: string) => {
    navigate({
      search: {
        page: currentPage,
        query: currentQuery || undefined,
        workspaceId,
        campaignPage: 1,
        campaignId: undefined,
      },
    });
  };

  const handleCampaignPageChange = (nextPage: number) => {
    if (!search.workspaceId) return;
    const safePage = Math.min(Math.max(1, nextPage), campaignTotalPages);
    navigate({
      search: {
        page: currentPage,
        query: currentQuery || undefined,
        workspaceId: search.workspaceId,
        campaignPage: safePage,
        campaignId: undefined,
      },
    });
  };

  const handleSelectCampaign = (campaignId: string) => {
    if (!search.workspaceId) return;
    navigate({
      search: {
        page: currentPage,
        query: currentQuery || undefined,
        workspaceId: search.workspaceId,
        campaignPage: currentCampaignPage,
        campaignId,
      },
    });
  };

  const handleEmailPageChange = (nextPage: number) => {
    if (!search.workspaceId) return;
    const safePage = Math.min(Math.max(1, nextPage), emailTotalPages);
    navigate({
      search: {
        page: currentPage,
        query: currentQuery || undefined,
        workspaceId: search.workspaceId,
        campaignPage: currentCampaignPage,
        campaignId: currentCampaignId,
        emailPage: safePage,
        emailDirection: currentEmailDirection,
      },
    });
  };

  const handleEmailDirectionChange = (direction: "outbound" | "inbound" | undefined) => {
    if (!search.workspaceId) return;
    navigate({
      search: {
        page: currentPage,
        query: currentQuery || undefined,
        workspaceId: search.workspaceId,
        campaignPage: currentCampaignPage,
        campaignId: currentCampaignId,
        emailPage: 1,
        emailDirection: direction,
      },
    });
  };

  const handleCloseCampaignDetail = () => {
    navigate({
      search: {
        page: currentPage,
        query: currentQuery || undefined,
        workspaceId: search.workspaceId,
        campaignPage: currentCampaignPage,
        campaignId: undefined,
        emailPage: currentEmailPage,
        emailDirection: currentEmailDirection,
      },
      replace: true,
    });
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalized = queryInput.trim();
    navigate({
      search: {
        page: 1,
        query: normalized || undefined,
        sortBy: currentSortBy,
        workspaceId: undefined,
        campaignPage: undefined,
        campaignId: undefined,
        emailPage: undefined,
        emailDirection: undefined,
      },
    });
  };

  const handleClearSearch = () => {
    setQueryInput("");
    navigate({
      search: {
        page: 1,
        query: undefined,
        sortBy: currentSortBy,
        workspaceId: undefined,
        campaignPage: undefined,
        campaignId: undefined,
        emailPage: undefined,
        emailDirection: undefined,
      },
    });
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)]">
      {/* Sidebar */}
      <aside className="flex h-full min-h-0 flex-col overflow-hidden border-b bg-muted/20 lg:border-b-0 lg:border-r">
        <div className="flex shrink-0 items-center justify-between border-b px-5 py-4">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">{t("workspaces.sidebar.title")}</div>
            <div className="text-xs text-muted-foreground">
              {t("workspaces.sidebar.total", { total: list.total })} •{" "}
              {t("workspaces.sidebar.pageInfo", { current: currentPage, total: totalPages })}
            </div>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {items.length}
          </Badge>
        </div>

        <div className="shrink-0 border-b px-5 py-3 space-y-2">
          <form onSubmit={handleSearchSubmit} className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-4 w-4 text-muted-foreground/70" />
            </div>
            <Input
              value={queryInput}
              onChange={(event) => setQueryInput(event.target.value)}
              placeholder={t("workspaces.sidebar.searchPlaceholder")}
              className="h-9 pl-9 pr-9 text-[14px] transition-all duration-150"
            />
            {queryInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground/70 transition-colors hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </form>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground mr-1">
              {t("workspaces.sidebar.sortBy")}:
            </span>
            <button
              type="button"
              onClick={() => handleSortByChange("campaign_count")}
              className={[
                "rounded px-2 py-0.5 text-xs transition-colors",
                currentSortBy === "campaign_count"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              ].join(" ")}
            >
              {t("workspaces.sidebar.sortByCampaigns")}
            </button>
            <button
              type="button"
              onClick={() => handleSortByChange("created_at")}
              className={[
                "rounded px-2 py-0.5 text-xs transition-colors",
                currentSortBy === "created_at"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              ].join(" ")}
            >
              {t("workspaces.sidebar.sortByDate")}
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="flex h-full items-center justify-center px-4 py-12 text-sm text-muted-foreground">
              {t("workspaces.sidebar.empty")}
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((item) => (
                <WorkspaceListRow
                  key={item.id}
                  item={item}
                  isSelected={search.workspaceId === item.id}
                  onSelect={handleSelectWorkspace}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t px-5 py-4">
          <div className="text-xs text-muted-foreground">
            {t("workspaces.sidebar.showing", { count: items.length, total: list.total })}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              {t("workspaces.sidebar.previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              {t("workspaces.sidebar.next")}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="relative min-h-0 min-w-0 overflow-hidden bg-background">
        {isDetailLoading ? (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-px overflow-hidden bg-border/80">
            <div className="h-full w-1/3 animate-pulse bg-gradient-to-r from-transparent via-primary/70 to-transparent" />
          </div>
        ) : null}
        <div className="h-full overflow-auto px-4 py-4 lg:px-6 lg:py-5">
          <WorkspaceDetailCard
            detail={detail}
            campaigns={campaigns}
            emails={emails}
            workspaceId={search.workspaceId}
            campaignPage={currentCampaignPage}
            campaignTotalPages={campaignTotalPages}
            emailPage={currentEmailPage}
            emailTotalPages={emailTotalPages}
            emailDirection={currentEmailDirection}
            onCampaignPageChange={handleCampaignPageChange}
            onSelectCampaign={handleSelectCampaign}
            onEmailPageChange={handleEmailPageChange}
            onEmailDirectionChange={handleEmailDirectionChange}
            campaignDetail={campaignDetail}
            campaignDetailError={campaignDetailError}
            campaignDetailId={currentCampaignId}
            onCampaignDetailUpdated={(nextDetail) => {
              setCampaignDetail(nextDetail);
              setCampaignDetailError(null);
            }}
            onCloseCampaignDetail={handleCloseCampaignDetail}
            isDetailLoading={isDetailLoading}
            isCampaignsLoading={isCampaignsLoading}
            isCampaignDetailLoading={isCampaignDetailLoading}
            isEmailsLoading={isEmailsLoading}
            onEmailClick={(email) => {
              setSelectedEmail(email);
              setIsEmailDetailOpen(true);
            }}
          />

          {/* Email Detail Dialog */}
          <EmailDetailDialog
            email={selectedEmail}
            open={isEmailDetailOpen}
            onOpenChange={setIsEmailDetailOpen}
            t={t}
          />
        </div>
      </main>
    </div>
  );
}

function WorkspaceListRow({
  item,
  isSelected,
  onSelect,
}: {
  item: AdminWorkspaceListItemResponse;
  isSelected: boolean;
  onSelect: (workspaceId: string) => void;
}) {
  const { t } = useTranslation("admin");
  const handleSelect = () => {
    onSelect(item.id);
  };

  return (
    <li>
      <button
        type="button"
        onClick={handleSelect}
        aria-pressed={isSelected}
        className={[
          "relative w-full text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          isSelected ? "bg-muted/60" : "hover:bg-muted/40",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary transition-opacity",
            isSelected ? "opacity-100" : "opacity-0",
          ].join(" ")}
        />
        <div className="flex items-start justify-between gap-3 px-5 py-4">
          <div className="min-w-0 space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="truncate text-sm font-medium">{item.display_name}</div>
              <SubscriptionBadge status={item.subscription_status} />
            </div>
            <div className="truncate text-xs text-muted-foreground">
              <span className="text-muted-foreground/80">{t("workspaces.sidebar.slug")}:</span>{" "}
              {item.slug}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              <span className="text-muted-foreground/80">{t("workspaces.sidebar.owner")}:</span>{" "}
              {item.owner_email || "-"}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground/90">
              <span>
                {t("workspaces.sidebar.created")}: {formatDate(item.created_at)}
              </span>
              <span>
                {t("workspaces.sidebar.lastActive")}: {formatDate(item.owner_last_activity_at)}
              </span>
            </div>
          </div>

          <div className="shrink-0">
            <span
              className={[
                "inline-flex h-8 items-center rounded-md border px-3 text-xs font-medium",
                isSelected
                  ? "border-transparent bg-secondary text-secondary-foreground"
                  : "border-border bg-background text-foreground",
              ].join(" ")}
            >
              {isSelected ? t("workspaces.sidebar.selected") : t("workspaces.sidebar.view")}
            </span>
          </div>
        </div>
      </button>
    </li>
  );
}

function WorkspaceDetailCard({
  detail,
  campaigns,
  emails,
  workspaceId,
  campaignPage,
  campaignTotalPages,
  emailPage,
  emailTotalPages,
  emailDirection,
  onCampaignPageChange,
  onSelectCampaign,
  onEmailPageChange,
  onEmailDirectionChange,
  campaignDetail,
  campaignDetailError,
  campaignDetailId,
  onCampaignDetailUpdated,
  onCloseCampaignDetail,
  isDetailLoading,
  isCampaignsLoading,
  isCampaignDetailLoading,
  isEmailsLoading,
  onEmailClick,
}: {
  detail: AdminWorkspaceDetailResponse | null;
  campaigns: AdminWorkspaceCampaignListResponse | null;
  emails: AdminWorkspaceEmailListResponse | null;
  workspaceId: string | undefined;
  campaignPage: number;
  campaignTotalPages: number;
  emailPage: number;
  emailTotalPages: number;
  emailDirection: "outbound" | "inbound" | undefined;
  onCampaignPageChange: (nextPage: number) => void;
  onSelectCampaign: (campaignId: string) => void;
  onEmailPageChange: (nextPage: number) => void;
  onEmailDirectionChange: (direction: "outbound" | "inbound" | undefined) => void;
  campaignDetail: AdminWorkspaceCampaignDetailResponse | null;
  campaignDetailError: string | null;
  campaignDetailId: string | undefined;
  onCampaignDetailUpdated: (detail: AdminWorkspaceCampaignDetailResponse) => void;
  onCloseCampaignDetail: () => void;
  isDetailLoading: boolean;
  isCampaignsLoading: boolean;
  isCampaignDetailLoading: boolean;
  isEmailsLoading: boolean;
  onEmailClick: (email: AdminWorkspaceEmailItemResponse) => void;
}) {
  const { t } = useTranslation("admin");

  if (isDetailLoading && !detail) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center rounded-xl border border-dashed text-sm text-muted-foreground">
        {t("workspaces.detail.loading")}
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center rounded-xl border border-dashed text-sm text-muted-foreground">
        {t("workspaces.detail.selectPrompt")}
      </div>
    );
  }

  const campaignMetrics = detail.campaigns as AdminWorkspaceDetailResponse["campaigns"] & {
    total_companies?: number;
    pending_companies?: number;
    contacted_companies?: number;
    qualified_companies?: number;
    rejected_companies?: number;
    emails_sent?: number;
    emails_replied?: number;
    pending_companies_last_updated_at?: string | null;
    contacted_companies_last_updated_at?: string | null;
    qualified_companies_last_updated_at?: string | null;
    rejected_companies_last_updated_at?: string | null;
    emails_sent_last_updated_at?: string | null;
    emails_replied_last_updated_at?: string | null;
  };
  const emailReplyRate =
    (campaignMetrics.emails_sent ?? 0) > 0
      ? Math.round(
          (((campaignMetrics.emails_replied ?? 0) as number) /
            ((campaignMetrics.emails_sent ?? 0) as number)) *
            100,
        )
      : 0;

  return (
    <div
      className={[
        "space-y-6 transition-opacity",
        isDetailLoading ? "opacity-85" : "opacity-100",
      ].join(" ")}
    >
      <header className="space-y-3 border-b pb-4">
        <div className="flex flex-col gap-4 2xl:grid 2xl:grid-cols-[minmax(0,560px)_minmax(0,1fr)] 2xl:items-start">
          <div className="flex min-w-0 items-start gap-3.5">
            {detail.logo_url ? (
              <img
                src={detail.logo_url}
                alt={`${detail.display_name} logo`}
                className="h-14 w-14 rounded-xl border border-border/70 object-cover"
              />
            ) : (
              <div className="h-14 w-14 rounded-xl border border-border/70 bg-muted" />
            )}
            <div className="min-w-0 space-y-1.5">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <Link
                  to="/workspace/$workspaceSlug/campaigns"
                  params={{ workspaceSlug: detail.slug }}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex min-w-0 items-center gap-1.5 text-3xl font-semibold tracking-tight text-foreground outline-none transition-colors hover:text-primary focus-visible:text-primary"
                  aria-label={`Open workspace ${detail.display_name}`}
                >
                  <span className="truncate underline-offset-4 group-hover:underline">
                    {detail.display_name}
                  </span>
                  <ExternalLink className="mt-1 size-5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70 group-focus-visible:opacity-70" />
                </Link>
                <MetaPill label={t("workspaces.detail.language")} value={detail.language} />
                <MetaPill label={t("workspaces.detail.timezone")} value={detail.timezone} />
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <MetaChip
                  label={t("workspaces.detail.workspace")}
                  value={detail.id}
                  copyValue={detail.id}
                  className="max-w-[640px]"
                />
              </div>
              <div className="pt-1 text-[11px] text-muted-foreground">
                <div className="flex flex-col gap-1">
                  <MetaChip
                    label={t("workspaces.detail.created")}
                    value={formatDate(detail.created_at)}
                  />
                  <MetaChip
                    label={t("workspaces.detail.updated")}
                    value={formatDate(detail.updated_at)}
                  />
                  <MetaChip
                    label={t("workspaces.detail.latestActive")}
                    value={formatDate(detail.latest_active_at)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <MiniInfoCard title={t("workspaces.detail.owner.title")}>
              <div className="grid grid-cols-2 gap-2">
                <CompactField
                  label={t("workspaces.detail.owner.name")}
                  value={detail.owner.display_name}
                />
                <CompactField
                  label={t("workspaces.detail.owner.email")}
                  value={detail.owner.email || "-"}
                />
                <div className="col-span-2">
                  <CompactField
                    label={t("workspaces.detail.owner.phone")}
                    value={detail.owner.phone_number || "-"}
                  />
                </div>
                <div className="col-span-2 pt-1">
                  <MetaChip
                    label={t("workspaces.detail.owner.title")}
                    value={detail.owner.id}
                    copyValue={detail.owner.id}
                    className="max-w-[280px]"
                    stacked
                  />
                </div>
              </div>
            </MiniInfoCard>

            <MiniInfoCard title={t("workspaces.detail.billing.title")}>
              <div className="grid grid-cols-2 gap-2">
                <CompactField
                  label={t("workspaces.detail.billing.status")}
                  value={<SubscriptionBadge status={detail.billing.status} />}
                />
                <CompactField
                  label={t("workspaces.detail.billing.plan")}
                  value={detail.billing.plan_type || "-"}
                />
                <CompactField
                  label={t("workspaces.detail.billing.cycle")}
                  value={detail.billing.billing_cycle || "-"}
                />
                <CompactField
                  label={t("workspaces.detail.billing.periodEnd")}
                  value={formatDate(detail.billing.current_period_end)}
                />
              </div>
              {/* Trial Extend Button */}
              <div className="mt-3 pt-3 border-t">
                <TrialExtendButton workspaceId={detail.id} billing={detail.billing} />
              </div>
            </MiniInfoCard>

            <MiniInfoCard title={t("workspaces.detail.campaigns.title")}>
              <div className="grid grid-cols-2 gap-2">
                <CompactField
                  label={t("workspaces.detail.campaigns.total")}
                  value={String(detail.campaigns.total)}
                />
                <CompactField
                  label={t("workspaces.detail.campaigns.active")}
                  value={String(detail.campaigns.active)}
                />
                <CompactField
                  label={t("workspaces.detail.campaigns.latestCreated")}
                  value={formatDate(detail.campaigns.latest_created_at)}
                />
                <CompactField
                  label={t("workspaces.detail.campaigns.latestUpdated")}
                  value={formatDate(detail.campaigns.latest_updated_at)}
                />
              </div>
            </MiniInfoCard>
          </div>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <WorkspaceStatCard
          icon={Building2}
          label={t("campaign.stats.totalCompanies")}
          value={campaignMetrics.total_companies ?? 0}
          color="blue"
        />
        <WorkspaceStatCard
          icon={Mail}
          label={t("campaign.stats.emailsSent")}
          value={campaignMetrics.emails_sent ?? 0}
          color="green"
        />
        <WorkspaceStatCard
          icon={Users}
          label={t("campaign.stats.qualifiedCompanies")}
          value={campaignMetrics.qualified_companies ?? 0}
          color="purple"
        />
        <WorkspaceStatCard
          icon={BarChart3}
          label={t("workspaces.detail.campaigns.active")}
          value={detail.campaigns.active}
          color="amber"
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("campaign.detail.companyStatusTitle")}
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <WorkspaceCompanyStatusCard
            status="pending"
            count={campaignMetrics.pending_companies ?? 0}
            total={campaignMetrics.total_companies ?? 0}
            lastUpdatedAt={campaignMetrics.pending_companies_last_updated_at}
            t={t}
          />
          <WorkspaceCompanyStatusCard
            status="contacted"
            count={campaignMetrics.contacted_companies ?? 0}
            total={campaignMetrics.total_companies ?? 0}
            lastUpdatedAt={campaignMetrics.contacted_companies_last_updated_at}
            t={t}
          />
          <WorkspaceCompanyStatusCard
            status="intent"
            count={campaignMetrics.emails_replied ?? 0}
            total={campaignMetrics.total_companies ?? 0}
            lastUpdatedAt={campaignMetrics.emails_replied_last_updated_at}
            t={t}
          />
          <WorkspaceCompanyStatusCard
            status="qualified"
            count={campaignMetrics.qualified_companies ?? 0}
            total={campaignMetrics.total_companies ?? 0}
            lastUpdatedAt={campaignMetrics.qualified_companies_last_updated_at}
            t={t}
          />
          <WorkspaceCompanyStatusCard
            status="rejected"
            count={campaignMetrics.rejected_companies ?? 0}
            total={campaignMetrics.total_companies ?? 0}
            lastUpdatedAt={campaignMetrics.rejected_companies_last_updated_at}
            t={t}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("campaign.detail.emailPerformanceTitle")}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <WorkspaceEmailMetricCard
            label={t("campaign.stats.emailsSent")}
            value={campaignMetrics.emails_sent ?? 0}
            lastUpdatedLabel={t("campaign.detail.metricLastUpdated", {
              value: formatDate(campaignMetrics.emails_sent_last_updated_at),
            })}
            icon={Mail}
            color="blue"
          />
          <WorkspaceEmailMetricCard
            label={t("campaign.stats.emailsReplied")}
            value={campaignMetrics.emails_replied ?? 0}
            subValue={`${emailReplyRate}%`}
            lastUpdatedLabel={t("campaign.detail.metricLastUpdated", {
              value: formatDate(campaignMetrics.emails_replied_last_updated_at),
            })}
            icon={BarChart3}
            color="purple"
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("campaign.detail.conversionFunnelTitle")}
        </h3>
        <div className="rounded-xl border border-border/80 bg-muted/20 p-4">
          <div className="space-y-4">
            <WorkspaceFunnelBar
              label={t("campaign.funnel.companiesFound")}
              value={campaignMetrics.total_companies ?? 0}
              max={campaignMetrics.total_companies ?? 0}
              color="bg-blue-500"
            />
            <WorkspaceFunnelBar
              label={t("campaign.funnel.contacted")}
              value={campaignMetrics.contacted_companies ?? 0}
              max={campaignMetrics.total_companies ?? 0}
              color="bg-amber-500"
            />
            <WorkspaceFunnelBar
              label={t("campaign.funnel.replied")}
              value={campaignMetrics.emails_replied ?? 0}
              max={campaignMetrics.total_companies ?? 0}
              color="bg-green-500"
            />
            <WorkspaceFunnelBar
              label={t("campaign.funnel.qualified")}
              value={campaignMetrics.qualified_companies ?? 0}
              max={campaignMetrics.total_companies ?? 0}
              color="bg-purple-500"
            />
          </div>
        </div>
      </section>

      <div className="divide-y divide-border/80">
        <section className="py-3">
          <DetailBlock title={t("workspaces.campaigns.title")}>
            <div className="space-y-3">
              {campaigns ? (
                <>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t("workspaces.campaigns.listTitle")}</span>
                    <span>{t("workspaces.campaigns.total", { total: campaigns.total })}</span>
                  </div>
                  <div className="overflow-hidden rounded-lg border border-border/80">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("workspaces.campaigns.table.name")}</TableHead>
                          <TableHead>{t("workspaces.campaigns.table.status")}</TableHead>
                          <TableHead>{t("workspaces.campaigns.table.created")}</TableHead>
                          <TableHead>{t("workspaces.campaigns.table.updated")}</TableHead>
                          <TableHead>{t("workspaces.campaigns.table.archived")}</TableHead>
                          <TableHead className="text-right">
                            {t("workspaces.campaigns.table.id")}
                          </TableHead>
                          <TableHead className="text-right">
                            {t("workspaces.campaigns.table.actions")}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaigns.items.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="py-10 text-center text-muted-foreground"
                            >
                              No campaigns found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          campaigns.items.map((campaign) => (
                            <TableRow key={campaign.id}>
                              <TableCell>
                                <div className="font-medium">{campaign.name || "-"}</div>
                              </TableCell>
                              <TableCell>
                                <CampaignStatusBadge status={campaign.status} />
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(campaign.created_at)}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(campaign.updated_at)}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(campaign.archived_at)}
                              </TableCell>
                              <TableCell className="text-right text-xs text-muted-foreground">
                                {campaign.id}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => onSelectCampaign(campaign.id)}
                                >
                                  {t("workspaces.campaigns.actions.details")}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {t("workspaces.campaigns.pageInfo", {
                        current: campaignPage,
                        total: campaignTotalPages,
                      })}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onCampaignPageChange(campaignPage - 1)}
                        disabled={campaignPage <= 1}
                      >
                        {t("workspaces.campaigns.previous")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onCampaignPageChange(campaignPage + 1)}
                        disabled={campaignPage >= campaignTotalPages}
                      >
                        {t("workspaces.campaigns.next")}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  {isCampaignsLoading
                    ? t("workspaces.campaigns.loading")
                    : t("workspaces.campaigns.unavailable")}
                </div>
              )}
            </div>
          </DetailBlock>
        </section>

        {/* Emails Section */}
        <section className="py-3">
          <DetailBlock title={t("workspaces.emails.title")}>
            <div className="space-y-3">
              {/* Direction Filter */}
              <div className="flex items-center gap-2">
                <Button
                  variant={emailDirection === undefined ? "default" : "outline"}
                  size="sm"
                  onClick={() => onEmailDirectionChange(undefined)}
                >
                  {t("workspaces.emails.filter.all")}
                </Button>
                <Button
                  variant={emailDirection === "outbound" ? "default" : "outline"}
                  size="sm"
                  onClick={() => onEmailDirectionChange("outbound")}
                >
                  {t("workspaces.emails.filter.outbound")}
                </Button>
                <Button
                  variant={emailDirection === "inbound" ? "default" : "outline"}
                  size="sm"
                  onClick={() => onEmailDirectionChange("inbound")}
                >
                  {t("workspaces.emails.filter.inbound")}
                </Button>
              </div>

              {emails ? (
                <>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t("workspaces.emails.listTitle")}</span>
                    <span>{t("workspaces.emails.total", { total: emails.total })}</span>
                  </div>
                  <div className="overflow-hidden rounded-lg border border-border/80">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("workspaces.emails.table.direction")}</TableHead>
                          <TableHead>{t("workspaces.emails.table.sender")}</TableHead>
                          <TableHead>{t("workspaces.emails.table.recipients")}</TableHead>
                          <TableHead>{t("workspaces.emails.table.subject")}</TableHead>
                          <TableHead>{t("workspaces.emails.table.preview")}</TableHead>
                          <TableHead>{t("workspaces.emails.table.sentAt")}</TableHead>
                          <TableHead className="text-right">
                            {t("workspaces.emails.table.messageId")}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emails.items.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="py-10 text-center text-muted-foreground"
                            >
                              {t("workspaces.emails.empty")}
                            </TableCell>
                          </TableRow>
                        ) : (
                          emails.items.map((email) => (
                            <TableRow
                              key={email.message_id}
                              className="cursor-pointer hover:bg-muted/50 transition-colors"
                              onClick={() => onEmailClick(email)}
                            >
                              <TableCell>
                                <EmailDirectionBadge direction={email.direction} />
                              </TableCell>
                              <TableCell>
                                <div className="max-w-[150px] truncate text-sm">
                                  {email.sender_display_name}
                                </div>
                                <div className="max-w-[150px] truncate text-xs text-muted-foreground">
                                  {email.sender_email}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="max-w-[150px] truncate text-xs text-muted-foreground">
                                  {email.recipient_emails && email.recipient_emails.length > 0
                                    ? `${email.recipient_emails.slice(0, 2).join(", ")}${
                                        email.recipient_emails.length > 2
                                          ? ` +${email.recipient_emails.length - 2}`
                                          : ""
                                      }`
                                    : "-"}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="max-w-[200px] truncate font-medium">
                                  {email.subject || "-"}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="max-w-[250px] truncate text-sm text-muted-foreground">
                                  {email.body_preview}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(email.sent_at ?? email.received_at ?? email.created_at)}
                              </TableCell>
                              <TableCell className="text-right text-xs text-muted-foreground">
                                {shortenId(email.message_id)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {t("workspaces.emails.pageInfo", {
                        current: emailPage,
                        total: emailTotalPages,
                      })}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEmailPageChange(emailPage - 1)}
                        disabled={emailPage <= 1}
                      >
                        {t("workspaces.emails.previous")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEmailPageChange(emailPage + 1)}
                        disabled={emailPage >= emailTotalPages}
                      >
                        {t("workspaces.emails.next")}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  {isEmailsLoading
                    ? t("workspaces.emails.loading")
                    : t("workspaces.emails.unavailable")}
                </div>
              )}
            </div>
          </DetailBlock>
        </section>
      </div>

      <CampaignDetailModal
        open={Boolean(campaignDetailId)}
        workspaceId={workspaceId}
        campaignId={campaignDetailId}
        detail={campaignDetail}
        error={campaignDetailError}
        isLoading={isCampaignDetailLoading}
        onDetailUpdated={onCampaignDetailUpdated}
        onOpenChange={(open) => {
          if (!open) {
            onCloseCampaignDetail();
          }
        }}
      />
    </div>
  );
}

function WorkspaceCompanyStatusCard({
  status,
  count,
  total,
  lastUpdatedAt,
  t,
}: {
  status: "pending" | "contacted" | "intent" | "qualified" | "rejected";
  count: number;
  total: number;
  lastUpdatedAt?: string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

  const config = {
    pending: {
      icon: Hourglass,
      color: "bg-amber-50 text-amber-700 border-amber-200",
      label: t("campaign.companyStatus.pending"),
    },
    contacted: {
      icon: Mail,
      color: "bg-blue-50 text-blue-700 border-blue-200",
      label: t("campaign.companyStatus.contacted"),
    },
    intent: {
      icon: Users,
      color: "bg-indigo-50 text-indigo-700 border-indigo-200",
      label: t("campaign.companyStatus.intent"),
    },
    qualified: {
      icon: CheckCircle,
      color: "bg-green-50 text-green-700 border-green-200",
      label: t("campaign.companyStatus.qualified"),
    },
    rejected: {
      icon: XCircle,
      color: "bg-red-50 text-red-700 border-red-200",
      label: t("campaign.companyStatus.rejected"),
    },
  };
  const { icon: Icon, color, label } = config[status];

  return (
    <div className={`rounded-xl border p-3 ${color}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 opacity-70" />
          <span className="text-[11px] font-medium opacity-80">{label}</span>
        </div>
        <span className="text-[11px] font-medium">{percentage}%</span>
      </div>
      <div className="mt-1 flex items-end justify-between gap-2">
        <div className="text-xl font-semibold">{count}</div>
        <div className="text-[10px] opacity-80">
          {t("workspaces.detail.metricLastUpdated", { value: formatDate(lastUpdatedAt) })}
        </div>
      </div>
    </div>
  );
}

function WorkspaceEmailMetricCard({
  label,
  value,
  subValue,
  lastUpdatedLabel,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  subValue?: string;
  lastUpdatedLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  color: "blue" | "green" | "purple" | "amber";
}) {
  const colorClasses = {
    blue: "text-blue-700",
    green: "text-green-700",
    purple: "text-purple-700",
    amber: "text-amber-700",
  };

  return (
    <div className="flex items-start gap-3 rounded-xl border border-border/80 bg-muted/20 p-3">
      <div className={`rounded-lg bg-background p-2 ${colorClasses[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 flex items-end justify-between gap-2">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-semibold">{value}</span>
            {subValue && (
              <span className={`text-sm font-medium ${colorClasses[color]}`}>{subValue}</span>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground">{lastUpdatedLabel}</div>
        </div>
      </div>
    </div>
  );
}

function WorkspaceStatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  color: "blue" | "green" | "purple" | "amber";
}) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };

  return (
    <div className={`rounded-xl border p-3 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 opacity-70" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function WorkspaceFunnelBar({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const percentage = max > 0 ? Math.round((value / max) * 100) : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {value} <span className="text-muted-foreground">({percentage}%)</span>
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function DetailBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function MiniInfoCard({ title, children }: { title: string; children: ReactNode }) {
  const toneClassName = "border-border/70 bg-muted/[0.18]";

  return (
    <section className={`min-w-0 space-y-2 rounded-2xl border p-3 ${toneClassName}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/90">
        {title}
      </div>
      <div className="grid grid-cols-1 gap-2">{children}</div>
    </section>
  );
}

function MetaPill({ label, value }: { label: string; value: string | null | undefined }) {
  const rendered = value && value.trim() !== "" ? value : "-";
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-muted/30 px-2 py-0.5 text-[11px] text-muted-foreground">
      <span className="text-muted-foreground/80">{label}:</span>
      <span className="max-w-[180px] truncate text-foreground/90">{rendered}</span>
    </div>
  );
}

function SubscriptionBadge({ status }: { status: string | null | undefined }) {
  const { t } = useTranslation("admin");

  if (!status) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (status === "trialing") {
    return (
      <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
        {t("workspaces.subscriptionStatus.trialing")}
      </Badge>
    );
  }

  if (status === "active") {
    return (
      <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
        {t("workspaces.subscriptionStatus.active")}
      </Badge>
    );
  }

  if (status === "canceled" || status === "expired") {
    return (
      <Badge variant="outline" className="bg-muted text-muted-foreground">
        {status === "canceled"
          ? t("workspaces.subscriptionStatus.canceled")
          : t("workspaces.subscriptionStatus.expired")}
      </Badge>
    );
  }

  return <Badge variant="outline">{status}</Badge>;
}

interface BusinessFormValues {
  name: string;
  location: string;
  website?: string;
  sellingPointsText?: string;
  productsText?: string;
}

interface IcpFormValues {
  searchQuery: string;
  source?: string;
}

function toMultiline(values: string[] | null | undefined): string {
  if (!values || values.length === 0) return "";
  return values.join("\n");
}

function buildBusinessDefaults(
  detail: AdminWorkspaceCampaignDetailResponse | null,
): BusinessFormValues {
  return {
    name: detail?.business?.name ?? "",
    location: detail?.business?.location ?? "",
    website: detail?.business?.website ?? "",
    sellingPointsText: toMultiline(detail?.business?.selling_points),
    productsText: toMultiline(detail?.business?.products),
  };
}

function buildIcpDefaults(detail: AdminWorkspaceCampaignDetailResponse | null): IcpFormValues {
  return {
    searchQuery: detail?.icp?.search_query ?? "",
    source: detail?.icp?.source ?? "",
  };
}

function shortenId(value: string | null | undefined, prefix = 8, suffix = 4): string {
  if (!value) return "-";
  if (value.length <= prefix + suffix + 1) return value;
  return `${value.slice(0, prefix)}…${value.slice(-suffix)}`;
}

function useCopyToClipboard() {
  const { t } = useTranslation("admin");

  return async (value: string, label: string): Promise<void> => {
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      toast.error(t("workspaces.clipboard.unavailable"));
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("workspaces.clipboard.copied", { label }));
    } catch {
      toast.error(t("workspaces.campaignDetail.errors.copyFailed"));
    }
  };
}

function MetaChip({
  label,
  value,
  copyValue,
  className,
  stacked = false,
}: {
  label: string;
  value: string;
  copyValue?: string;
  className?: string;
  stacked?: boolean;
}) {
  const copyToClipboard = useCopyToClipboard();
  const rendered = value.trim() === "" ? "-" : value;

  const valueRow = (
    <div className="inline-flex min-w-0 items-center gap-1.5">
      <span className="min-w-0 truncate whitespace-nowrap font-mono text-foreground/90">
        {rendered}
      </span>
      {copyValue ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={() => void copyToClipboard(copyValue, `${label} ID`)}
          aria-label={`Copy ${label} ID`}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  );

  return (
    <div
      className={[
        stacked
          ? "flex min-w-0 flex-col items-start gap-0.5 text-[11px] text-muted-foreground"
          : "inline-flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground",
        className ?? "",
      ].join(" ")}
    >
      <span
        className={
          stacked
            ? "uppercase tracking-wide text-muted-foreground/80"
            : "shrink-0 uppercase tracking-wide text-muted-foreground/80"
        }
      >
        {label}
      </span>
      {stacked ? valueRow : valueRow}
    </div>
  );
}

function SectionCard({ children }: { children: ReactNode }) {
  return <section className="rounded-2xl bg-background p-5">{children}</section>;
}

function SectionHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="space-y-1">
        <div className="text-[13px] font-semibold tracking-tight text-foreground">{title}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm leading-6 text-foreground/90">{value}</div>
    </div>
  );
}

function CompactField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/90">
        {label}
      </div>
      <div className="min-w-0 truncate whitespace-nowrap text-sm leading-5 text-foreground/90">
        {value}
      </div>
    </div>
  );
}

function CampaignDetailModal({
  open,
  workspaceId,
  campaignId,
  detail,
  error,
  isLoading,
  onDetailUpdated,
  onOpenChange,
}: {
  open: boolean;
  workspaceId: string | undefined;
  campaignId: string | undefined;
  detail: AdminWorkspaceCampaignDetailResponse | null;
  error: string | null;
  isLoading: boolean;
  onDetailUpdated: (detail: AdminWorkspaceCampaignDetailResponse) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("admin");
  const title = detail?.name || t("workspaces.campaignDetail.title");
  const subtitle =
    detail?.id ??
    campaignId ??
    (isLoading ? t("workspaces.campaignDetail.loading") : t("workspaces.campaignDetail.title"));

  const businessFormSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t("workspaces.campaignDetail.validation.businessNameRequired")),
        location: z
          .string()
          .trim()
          .min(1, t("workspaces.campaignDetail.validation.locationRequired")),
        website: z.string().trim().optional(),
        sellingPointsText: z.string().optional(),
        productsText: z.string().optional(),
      }),
    [t],
  );

  const icpFormSchema = useMemo(
    () =>
      z.object({
        searchQuery: z
          .string()
          .trim()
          .min(1, t("workspaces.campaignDetail.validation.searchQueryRequired")),
        source: z.string().trim().optional(),
      }),
    [t],
  );

  const [isBusinessEditing, setIsBusinessEditing] = useState<boolean>(false);
  const [isIcpEditing, setIsIcpEditing] = useState<boolean>(false);
  const [isBusinessSaving, setIsBusinessSaving] = useState<boolean>(false);
  const [isIcpSaving, setIsIcpSaving] = useState<boolean>(false);
  const [sellingPointsLines, setSellingPointsLines] = useState<string[]>([]);
  const [productLines, setProductLines] = useState<string[]>([]);

  const businessForm = useForm<BusinessFormValues>({
    resolver: zodResolver(businessFormSchema),
    defaultValues: buildBusinessDefaults(detail),
  });
  const icpForm = useForm<IcpFormValues>({
    resolver: zodResolver(icpFormSchema),
    defaultValues: buildIcpDefaults(detail),
  });

  useEffect(() => {
    businessForm.reset(buildBusinessDefaults(detail));
    icpForm.reset(buildIcpDefaults(detail));
    setIsBusinessEditing(false);
    setIsIcpEditing(false);
    setSellingPointsLines(
      detail?.business?.selling_points?.length ? detail.business.selling_points : [""],
    );
    setProductLines(detail?.business?.products?.length ? detail.business.products : [""]);
  }, [businessForm, detail, icpForm]);

  const handleBusinessEdit = () => {
    businessForm.reset(buildBusinessDefaults(detail));
    setSellingPointsLines(
      detail?.business?.selling_points?.length ? detail.business.selling_points : [""],
    );
    setProductLines(detail?.business?.products?.length ? detail.business.products : [""]);
    setIsBusinessEditing(true);
  };

  const handleBusinessCancel = () => {
    businessForm.reset(buildBusinessDefaults(detail));
    setSellingPointsLines(
      detail?.business?.selling_points?.length ? detail.business.selling_points : [""],
    );
    setProductLines(detail?.business?.products?.length ? detail.business.products : [""]);
    setIsBusinessEditing(false);
  };

  const handleIcpEdit = () => {
    icpForm.reset(buildIcpDefaults(detail));
    setIsIcpEditing(true);
  };

  const handleIcpCancel = () => {
    icpForm.reset(buildIcpDefaults(detail));
    setIsIcpEditing(false);
  };

  const resolveCampaignIds = (): {
    resolvedWorkspaceId: string;
    resolvedCampaignId: string;
  } | null => {
    const resolvedWorkspaceId = workspaceId ?? detail?.workspace_id;
    const resolvedCampaignId = campaignId ?? detail?.id;
    if (!resolvedWorkspaceId || !resolvedCampaignId) {
      toast.error(t("workspaces.campaignDetail.errors.missingIds"));
      return null;
    }
    return { resolvedWorkspaceId, resolvedCampaignId };
  };

  const handleBusinessSubmit = businessForm.handleSubmit(async (values) => {
    const ids = resolveCampaignIds();
    if (!ids) return;

    setIsBusinessSaving(true);
    try {
      const updated = await updateAdminWorkspaceCampaignBusinessServer({
        data: {
          workspaceId: ids.resolvedWorkspaceId,
          campaignId: ids.resolvedCampaignId,
          payload: {
            name: values.name.trim(),
            location: values.location.trim(),
            website: values.website && values.website.trim() !== "" ? values.website.trim() : null,
            selling_points: sellingPointsLines
              .map((item) => item.trim())
              .filter((item) => item.length > 0),
            products: productLines.map((item) => item.trim()).filter((item) => item.length > 0),
          },
        },
      });

      onDetailUpdated(updated as AdminWorkspaceCampaignDetailResponse);
      toast.success(t("workspaces.campaignDetail.business.updateSuccess"));
      setIsBusinessEditing(false);
    } catch (submitError) {
      const message = extractApiErrorMessage(
        submitError,
        t("workspaces.campaignDetail.business.updateError"),
      );
      logError("admin.workspaces.campaign_business_update_failed", {
        workspace_id: ids.resolvedWorkspaceId,
        campaign_id: ids.resolvedCampaignId,
        error: message,
      });
      toast.error(message);
    } finally {
      setIsBusinessSaving(false);
    }
  });

  const handleIcpSubmit = icpForm.handleSubmit(async (values) => {
    const resolvedWorkspaceId = workspaceId ?? detail?.workspace_id;
    const resolvedCampaignId = campaignId ?? detail?.id;
    if (!resolvedWorkspaceId || !resolvedCampaignId) {
      toast.error("Campaign identifiers are missing.");
      return;
    }

    setIsIcpSaving(true);
    try {
      const updated = await updateAdminWorkspaceCampaignIcpServer({
        data: {
          workspaceId: resolvedWorkspaceId,
          campaignId: resolvedCampaignId,
          payload: {
            search_query: values.searchQuery.trim(),
            source: detail?.icp?.source ?? null,
          },
        },
      });

      onDetailUpdated(updated as AdminWorkspaceCampaignDetailResponse);
      toast.success(t("workspaces.campaignDetail.icp.updateSuccess"));
      setIsIcpEditing(false);
    } catch (submitError) {
      const message = extractApiErrorMessage(
        submitError,
        t("workspaces.campaignDetail.icp.updateError"),
      );
      logError("admin.workspaces.campaign_icp_update_failed", {
        workspace_id: resolvedWorkspaceId,
        campaign_id: resolvedCampaignId,
        error: message,
      });
      toast.error(message);
    } finally {
      setIsIcpSaving(false);
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full gap-0 overflow-hidden p-0 sm:w-[1240px] sm:max-w-[1240px]">
        <div className="flex max-h-[92vh] flex-col">
          <DialogHeader className="space-y-4 border-b bg-background px-6 py-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <DialogTitle className="text-[22px] font-semibold leading-tight tracking-[-0.01em]">
                    {title}
                  </DialogTitle>
                  {detail ? (
                    <>
                      <CampaignStatusBadge status={detail.status} />
                      {detail.archived_at ? (
                        <Badge
                          variant="outline"
                          className="border-border bg-muted text-muted-foreground"
                        >
                          {t("workspaces.campaignDetail.archived")}
                        </Badge>
                      ) : null}
                    </>
                  ) : null}
                </div>
                <DialogDescription className="text-xs text-muted-foreground">
                  {t("workspaces.campaignDetail.title")}
                </DialogDescription>
                <div className="text-[11px] font-mono text-muted-foreground/90">{subtitle}</div>
              </div>
            </div>

            {detail ? (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
                <MetaChip
                  label={t("workspaces.campaignDetail.meta.campaign")}
                  value={shortenId(detail.id)}
                  copyValue={detail.id}
                />
                <MetaChip
                  label={t("workspaces.campaignDetail.meta.workspace")}
                  value={shortenId(detail.workspace_id)}
                  copyValue={detail.workspace_id}
                />
                <MetaChip
                  label={t("workspaces.campaignDetail.meta.creator")}
                  value={shortenId(detail.creator_id)}
                  copyValue={detail.creator_id}
                />
                <MetaChip
                  label={t("workspaces.campaignDetail.meta.updated")}
                  value={formatDate(detail.updated_at)}
                />
              </div>
            ) : null}
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-auto bg-background">
            {!detail ? (
              <div className="space-y-3 px-6 py-6">
                <div className="text-sm text-muted-foreground">
                  {isLoading
                    ? t("workspaces.campaignDetail.loading")
                    : t("workspaces.campaignDetail.unavailable")}
                </div>
                {error ? (
                  <div className="text-xs text-destructive">
                    {t("workspaces.campaignDetail.error")}: {error}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="px-6 py-6">
                <div className="divide-y divide-border/60 overflow-hidden rounded-2xl border border-border/70 bg-background">
                  <SectionCard>
                    <div className="space-y-5">
                      <SectionHeader
                        title={t("workspaces.campaignDetail.business.title")}
                        description={t("workspaces.campaignDetail.business.description")}
                        actions={
                          isBusinessEditing ? (
                            <>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleBusinessCancel}
                                disabled={isBusinessSaving}
                              >
                                {t("workspaces.campaignDetail.business.cancel")}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleBusinessSubmit}
                                disabled={isBusinessSaving}
                              >
                                {isBusinessSaving
                                  ? t("workspaces.campaignDetail.business.saving")
                                  : t("workspaces.campaignDetail.business.save")}
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={handleBusinessEdit}
                              aria-label={t("workspaces.campaignDetail.business.edit")}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )
                        }
                      />

                      {isBusinessEditing ? (
                        <form className="space-y-5">
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-1.5">
                              <label
                                htmlFor="business-name"
                                className="text-xs font-medium text-muted-foreground"
                              >
                                {t("workspaces.campaignDetail.business.fields.name")}
                              </label>
                              <Input
                                id="business-name"
                                {...businessForm.register("name")}
                                placeholder={t(
                                  "workspaces.campaignDetail.business.placeholders.name",
                                )}
                              />
                              {businessForm.formState.errors.name ? (
                                <div className="text-xs text-destructive">
                                  {businessForm.formState.errors.name.message}
                                </div>
                              ) : null}
                            </div>
                            <div className="space-y-1.5">
                              <label
                                htmlFor="business-location"
                                className="text-xs font-medium text-muted-foreground"
                              >
                                {t("workspaces.campaignDetail.business.fields.location")}
                              </label>
                              <Input
                                id="business-location"
                                {...businessForm.register("location")}
                                placeholder={t(
                                  "workspaces.campaignDetail.business.placeholders.location",
                                )}
                              />
                              {businessForm.formState.errors.location ? (
                                <div className="text-xs text-destructive">
                                  {businessForm.formState.errors.location.message}
                                </div>
                              ) : null}
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <label
                              htmlFor="business-website"
                              className="text-xs font-medium text-muted-foreground"
                            >
                              {t("workspaces.campaignDetail.business.fields.website")}
                            </label>
                            <Input
                              id="business-website"
                              {...businessForm.register("website")}
                              placeholder={t(
                                "workspaces.campaignDetail.business.placeholders.website",
                              )}
                            />
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-xs font-medium text-muted-foreground">
                                {t("workspaces.campaignDetail.business.fields.sellingPoints")}
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setSellingPointsLines((prev) => [...prev, ""])}
                                disabled={isBusinessSaving}
                              >
                                {t("workspaces.campaignDetail.business.actions.addLine")}
                              </Button>
                            </div>
                            <div className="space-y-2">
                              {sellingPointsLines.map((line, index) => (
                                <Input
                                  key={`selling-${index}`}
                                  value={line}
                                  onChange={(event) =>
                                    setSellingPointsLines((prev) =>
                                      prev.map((item, itemIndex) =>
                                        itemIndex === index ? event.target.value : item,
                                      ),
                                    )
                                  }
                                  placeholder={t(
                                    "workspaces.campaignDetail.business.placeholders.sellingPoint",
                                    { index: index + 1 },
                                  )}
                                  aria-label={t(
                                    "workspaces.campaignDetail.business.placeholders.sellingPoint",
                                    { index: index + 1 },
                                  )}
                                  disabled={isBusinessSaving}
                                />
                              ))}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {t("workspaces.campaignDetail.business.hints.clearToRemove")}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-xs font-medium text-muted-foreground">
                                {t("workspaces.campaignDetail.business.fields.products")}
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setProductLines((prev) => [...prev, ""])}
                                disabled={isBusinessSaving}
                              >
                                {t("workspaces.campaignDetail.business.actions.addLine")}
                              </Button>
                            </div>
                            <div className="space-y-2">
                              {productLines.map((line, index) => (
                                <Input
                                  key={`product-${index}`}
                                  value={line}
                                  onChange={(event) =>
                                    setProductLines((prev) =>
                                      prev.map((item, itemIndex) =>
                                        itemIndex === index ? event.target.value : item,
                                      ),
                                    )
                                  }
                                  placeholder={t(
                                    "workspaces.campaignDetail.business.placeholders.product",
                                    { index: index + 1 },
                                  )}
                                  aria-label={t(
                                    "workspaces.campaignDetail.business.placeholders.product",
                                    { index: index + 1 },
                                  )}
                                  disabled={isBusinessSaving}
                                />
                              ))}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {t("workspaces.campaignDetail.business.hints.clearToRemove")}
                            </div>
                          </div>
                        </form>
                      ) : detail.business ? (
                        (() => {
                          const business = detail.business;
                          return (
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                              <DetailItem
                                label={t("workspaces.campaignDetail.business.fields.name")}
                                value={business.name}
                              />
                              <DetailItem
                                label={t("workspaces.campaignDetail.business.fields.location")}
                                value={business.location}
                              />
                              <DetailItem
                                label={t("workspaces.campaignDetail.business.fields.email")}
                                value={business.email || "-"}
                              />
                              <DetailItem
                                label={t("workspaces.campaignDetail.business.fields.phone")}
                                value={business.phone_number || "-"}
                              />
                              <div className="md:col-span-2">
                                <DetailItem
                                  label={t("workspaces.campaignDetail.business.fields.website")}
                                  value={
                                    business.website ? (
                                      <a
                                        href={business.website}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1.5 text-foreground underline-offset-4 hover:underline"
                                      >
                                        {business.website}
                                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                                      </a>
                                    ) : (
                                      "-"
                                    )
                                  }
                                />
                              </div>
                              <div className="md:col-span-2">
                                <DetailItem
                                  label={t(
                                    "workspaces.campaignDetail.business.fields.sellingPoints",
                                  )}
                                  value={
                                    business.selling_points.length > 0 ? (
                                      <ul className="list-disc space-y-1.5 pl-4">
                                        {business.selling_points.map((item, index) => (
                                          <li
                                            key={`selling-${index}`}
                                            className="text-sm leading-6 text-foreground/90"
                                          >
                                            {item}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      "-"
                                    )
                                  }
                                />
                              </div>
                              <div className="md:col-span-2">
                                <DetailItem
                                  label={t("workspaces.campaignDetail.business.fields.products")}
                                  value={
                                    business.products.length > 0 ? (
                                      <ul className="list-disc space-y-1.5 pl-4">
                                        {business.products.map((item, index) => (
                                          <li
                                            key={`product-${index}`}
                                            className="text-sm leading-6 text-foreground/90"
                                          >
                                            {item}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      "-"
                                    )
                                  }
                                />
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="rounded-xl border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
                          {t("workspaces.campaignDetail.business.empty")}
                        </div>
                      )}
                    </div>
                  </SectionCard>

                  <SectionCard>
                    <div className="space-y-5">
                      <SectionHeader
                        title={t("workspaces.campaignDetail.icp.title")}
                        description={t("workspaces.campaignDetail.icp.description")}
                        actions={
                          isIcpEditing ? (
                            <>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleIcpCancel}
                                disabled={isIcpSaving}
                              >
                                {t("workspaces.campaignDetail.icp.cancel")}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleIcpSubmit}
                                disabled={isIcpSaving}
                              >
                                {isIcpSaving
                                  ? t("workspaces.campaignDetail.icp.saving")
                                  : t("workspaces.campaignDetail.icp.save")}
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={handleIcpEdit}
                              aria-label={t("workspaces.campaignDetail.icp.edit")}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )
                        }
                      />

                      {isIcpEditing ? (
                        <form className="space-y-4">
                          <div className="space-y-1.5">
                            <label
                              htmlFor="icp-search-query"
                              className="text-xs font-medium text-muted-foreground"
                            >
                              {t("workspaces.campaignDetail.icp.fields.searchQuery")}
                            </label>
                            <Textarea
                              id="icp-search-query"
                              {...icpForm.register("searchQuery")}
                              placeholder={t(
                                "workspaces.campaignDetail.icp.placeholders.searchQuery",
                              )}
                              className="min-h-[180px]"
                            />
                            {icpForm.formState.errors.searchQuery ? (
                              <div className="text-xs text-destructive">
                                {icpForm.formState.errors.searchQuery.message}
                              </div>
                            ) : null}
                          </div>
                          {detail.icp ? (
                            <DetailItem
                              label={t("workspaces.campaignDetail.icp.fields.source")}
                              value={detail.icp.source || "-"}
                            />
                          ) : null}
                        </form>
                      ) : detail.icp ? (
                        <div className="space-y-4">
                          <DetailItem
                            label={t("workspaces.campaignDetail.icp.fields.source")}
                            value={detail.icp.source || "-"}
                          />
                          <div className="space-y-1.5">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              {t("workspaces.campaignDetail.icp.fields.searchQuery")}
                            </div>
                            <div className="whitespace-pre-wrap rounded-xl border border-border/80 bg-background px-4 py-3 text-sm leading-7 text-foreground/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
                              {detail.icp.search_query}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
                          {t("workspaces.campaignDetail.icp.empty")}
                        </div>
                      )}
                    </div>
                  </SectionCard>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CampaignStatusBadge({ status }: { status: string | null | undefined }) {
  const { t } = useTranslation("admin");

  if (!status) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (status === "active") {
    return (
      <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
        {t("workspaces.campaignStatus.active")}
      </Badge>
    );
  }

  if (status === "paused") {
    return (
      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
        {t("workspaces.campaignStatus.paused")}
      </Badge>
    );
  }

  if (status === "archived") {
    return (
      <Badge variant="outline" className="bg-muted text-muted-foreground">
        {t("workspaces.campaignStatus.archived")}
      </Badge>
    );
  }

  if (status === "draft") {
    return (
      <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
        {t("workspaces.campaignStatus.draft")}
      </Badge>
    );
  }

  return <Badge variant="outline">{status}</Badge>;
}

function EmailDirectionBadge({ direction }: { direction: "outbound" | "inbound" }) {
  const { t } = useTranslation("admin");

  if (direction === "outbound") {
    return (
      <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
        {t("workspaces.emailDirection.outbound")}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
      {t("workspaces.emailDirection.inbound")}
    </Badge>
  );
}

function parseErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    try {
      const parsed = JSON.parse(error.message) as { detail?: string; status?: number };
      if (parsed.detail) {
        return parsed.status ? `${parsed.detail} (status ${parsed.status})` : parsed.detail;
      }
    } catch {
      return error.message;
    }
    return error.message;
  }
  return "unknown-error";
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const formatter = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZoneName: "short",
  });

  const parts = formatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";

  const year = pick("year");
  const month = pick("month");
  const day = pick("day");
  const hour = pick("hour");
  const minute = pick("minute");
  const second = pick("second");
  const timeZoneName = pick("timeZoneName");

  const core = `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  return timeZoneName ? `${core} ${timeZoneName}` : core;
}

// ============================================================================
// Trial Extension Components
// ============================================================================

interface TrialExtendButtonProps {
  workspaceId: string;
  billing: AdminWorkspaceDetailResponse["billing"];
}

function TrialExtendButton({ workspaceId, billing }: TrialExtendButtonProps) {
  const { t } = useTranslation("admin");
  const [dialogOpen, setDialogOpen] = useState(false);

  const canExtend =
    billing.status === "trialing" ||
    billing.status === "expired" ||
    billing.status === "canceled" ||
    !billing.status;

  if (!canExtend) {
    return null;
  }

  return (
    <>
      <Button variant="outline" size="sm" className="w-full" onClick={() => setDialogOpen(true)}>
        <CalendarClock className="mr-2 h-4 w-4" />
        {t("workspaces.detail.billing.extendTrial")}
      </Button>

      <TrialExtendDialog workspaceId={workspaceId} open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}

interface TrialExtendDialogProps {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function TrialExtendDialog({ workspaceId, open, onOpenChange }: TrialExtendDialogProps) {
  const { t } = useTranslation("admin");
  const [trialStatus, setTrialStatus] = useState<TrialStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [days, setDays] = useState<number>(14);
  const [reason, setReason] = useState("");

  // Load trial status when dialog opens
  useEffect(() => {
    if (!open || !workspaceId) {
      setTrialStatus(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getWorkspaceTrialStatusServer({ data: { workspaceId } })
      .then((result) => {
        if (cancelled) return;
        setTrialStatus(result as TrialStatusResponse);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logError("admin.trial_dialog.load_failed", {
          workspace_id: workspaceId,
          error: error instanceof Error ? error.message : "unknown",
        });
        toast.error(t("workspaces.trialDialog.loadError"));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, t]);

  const handleSubmit = async () => {
    if (!workspaceId || days < 1 || days > 90) {
      toast.error(t("workspaces.trialDialog.invalidDays"));
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await setWorkspaceTrialServer({
        data: {
          workspaceId,
          days,
          reason: reason.trim() || undefined,
        },
      });

      const trialResult = result as TrialSetResponse;
      toast.success(
        t("workspaces.trialDialog.success", {
          operation: t(`workspaces.trialDialog.operation.${trialResult.operation}`),
        }),
      );
      onOpenChange(false);
      // Reset form
      setDays(14);
      setReason("");
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? extractApiErrorMessage({ data: { detail: error.message } }, "Failed to set trial")
          : "Failed to set trial";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusDisplay = (status: string | null | undefined) => {
    if (!status) return t("workspaces.trialStatus.none");
    return t(`workspaces.trialStatus.${status}`) || status;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t("workspaces.trialDialog.title")}</DialogTitle>
          <DialogDescription>{t("workspaces.trialDialog.description")}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t("workspaces.trialDialog.loading")}
          </div>
        ) : trialStatus ? (
          <div className="space-y-4 py-2">
            {/* Current Status */}
            <div className="rounded-lg bg-muted p-3">
              <div className="text-sm font-medium">{t("workspaces.trialDialog.currentStatus")}</div>
              <div className="mt-1 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("workspaces.trialDialog.status")}
                  </span>
                  <span className="font-medium">{getStatusDisplay(trialStatus.status)}</span>
                </div>
                {trialStatus.trial_end && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("workspaces.trialDialog.trialEnd")}
                    </span>
                    <span>{formatDate(trialStatus.trial_end)}</span>
                  </div>
                )}
                {trialStatus.days_remaining !== null && trialStatus.days_remaining >= 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("workspaces.trialDialog.daysRemaining")}
                    </span>
                    <span
                      className={
                        trialStatus.days_remaining <= 3 ? "text-destructive font-medium" : ""
                      }
                    >
                      {t("workspaces.trialDialog.daysCount", {
                        count: trialStatus.days_remaining,
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Days Input */}
            <div className="space-y-2">
              <label htmlFor="trial-days" className="text-sm font-medium">
                {t("workspaces.trialDialog.daysLabel")} *
              </label>
              <Input
                id="trial-days"
                type="number"
                min={1}
                max={90}
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10) || 0)}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                {t("workspaces.trialDialog.daysHint")}
              </p>
            </div>

            {/* Reason Input */}
            <div className="space-y-2">
              <label htmlFor="trial-reason" className="text-sm font-medium">
                {t("workspaces.trialDialog.reasonLabel")}
              </label>
              <Textarea
                id="trial-reason"
                placeholder={t("workspaces.trialDialog.reasonPlaceholder")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[80px] resize-none"
                maxLength={500}
              />
              <p className="text-right text-xs text-muted-foreground">{reason.length}/500</p>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t("workspaces.trialDialog.loadError")}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t("workspaces.trialDialog.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || isSubmitting || days < 1 || days > 90}
          >
            {isSubmitting
              ? t("workspaces.trialDialog.submitting")
              : t("workspaces.trialDialog.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
